/**
 * 📌 Script: i18nVitrine.js
 * 📂 Caminho: src/i18nVitrine.js
 * 📝 Descrição: i18n da Vitrine com descoberta automática (require.context)
 *   e carga preguiçosa por idioma. Fallback "de". Cache em "vitrine.lang".
 *
 * 🔄 Relação com o Projeto:
 *  - Importado em main.js antes do mount (registerVitrineMessages).
 *  - Usado no header para listar e trocar idioma sem afetar telas internas.
 *
 * 📥 Entradas (Parâmetros):
 *  - `i18n` (instância do createI18n) em registerVitrineMessages.
 *  - `composer` (useI18n()) em ensure/set no componente.
 *
 * 📤 Saídas:
 *  - Registro inicial do fallback.
 *  - Lista de locales detectados.
 *  - Utilitários de exibição e troca de idioma.
 *
 * 🧩 Exports:
 *  - `registerVitrineMessages(i18n)`
 *  - `getVitrineLocales()`
 *  - `displayName(code, ui)`
 *  - `ensureVitrineLocaleComposer(composer, code)`
 *  - `setVitrineLocaleComposer(composer, code)`
 *  - `getCachedVitrineLocale()`
 *
 * 🔗 Dependências:
 *  - vue-i18n v11 (composer.locale é Ref).
 */

import vitrineDe from "./locales/vitrine-de.json";

// 🔎 Descoberta automática dos arquivos vitrine-*.json (lazy chunks)
const ctx = require.context(
  "./locales",
  false,
  /^\.\/vitrine-[a-z]{2}\.json$/,
  "lazy"
);

/** Extrai "xx" de "./vitrine-xx.json". */
function codeFromPath(p) {
  const m = p.match(/vitrine-([a-z]{2})\.json$/i);
  return m ? m[1].toLowerCase() : null;
}

// 🔧 Normaliza locale vindo como string, Ref ou indefinido
function normalizeLocale(input) {
  if (typeof input === "string") return input.toLowerCase();
  if (input && typeof input === "object" && "value" in input) {
    const v = input.value;
    return typeof v === "string" ? v.toLowerCase() : "de";
  }
  return "de";
}

/** Lista ordenada de locales detectados. */
const AVAILABLE = Array.from(
  new Set(
    Object.keys(ctx.keys ? ctx : {}).length
      ? ctx.keys().map(codeFromPath).filter(Boolean)
      : []
  )
).sort();

/** Cache local exclusivo da Vitrine. */
function readCache() {
  try {
    return localStorage.getItem("vitrine.lang") || null;
  } catch (_) {
    return null;
  }
}
function writeCache(code) {
  try {
    localStorage.setItem("vitrine.lang", code);
  } catch (_) {
    /* ignore */
  }
}

export function getCachedVitrineLocale() {
  return readCache();
}

/** Nome exibido do idioma. Fallback para o próprio código. */
export function displayName(code, ui = "de") {
  try {
    const dn = new Intl.DisplayNames(ui, { type: "language" });
    return dn.of(code) || code;
  } catch (_) {
    return code;
  }
}

/** Registra apenas o fallback "de" no i18n global. */
import { watch } from "vue";

export function registerVitrineMessages(i18n) {
  i18n.global.mergeLocaleMessage("de", vitrineDe);

  // carrega msgs Vitrine do locale atual e de futuras trocas
  const load = async (lc) => {
    const c = normalizeLocale(lc);
    if (c !== "de" && AVAILABLE.includes(c)) {
      const mod = await ctx(`./vitrine-${c}.json`);
      const msgs = mod?.default || mod;
      i18n.global.mergeLocaleMessage(c, msgs);
    }
  };

  // inicial + reatividade (locale é Ref)
  load(i18n.global.locale?.value || i18n.global.locale);
  watch(i18n.global.locale, (nv) => load(nv));
}

/** Lista de idiomas disponíveis detectados. */
export function getVitrineLocales() {
  return AVAILABLE.slice();
}

/**
 * Garante que o JSON do idioma foi mesclado no composer.
 * Usa lazy import do chunk correspondente.
 */
export async function ensureVitrineLocaleComposer(composer, code) {
  const c = normalizeLocale(code);
  if (!AVAILABLE.includes(c)) return;
  const has =
    composer.getLocaleMessage &&
    Object.keys(composer.getLocaleMessage(c) || {}).length > 0;
  if (has) return;

  const mod = await ctx(`./vitrine-${c}.json`);
  const msgs = mod?.default || mod;
  composer.mergeLocaleMessage(c, msgs);
}

/**
 * Troca o idioma no composer, garantindo mensagens e
 * persistindo o cache exclusivo da Vitrine.
 */
export async function setVitrineLocaleComposer(composer, code) {
  const c = normalizeLocale(code);
  await ensureVitrineLocaleComposer(composer, c);
  composer.locale.value = c;
  writeCache(c);
}

/** Heurística inicial para o boot global (fallback "de"). */
export function detectInitialLocale() {
  try {
    const nav = (navigator?.language || "de").toLowerCase();
    if (nav.startsWith("pt")) return "pt";
    if (nav.startsWith("de")) return "de";
  } catch (_) {
    /* ignore */
  }
  return "de";
}
